<template>
  <v-sheet color="white" class="ma-0 pa-0 rounded-lg shadow">
    <v-container ref="bp_container" fluid fill-width class="ma-0 pa-0">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <div
            v-if="!outcomeVar"
            class="display-1 primary--text text--lighten-5 pt-5 mt-5"
            align="center"
          >
            NO VARIABLE SELECTED
          </div>

          <div
            v-else-if="!cohorts || cohorts.length == 0"
            class="display-1 primary--text text--lighten-5 pt-5 mt-5"
            align="center"
          >
            NO STUDY GROUPS SELECTED
          </div>

          <div v-else class="ttWrapper">
            <div
              ref="echartContainer"
              :style="
                'width: 100%; height: 100%; min-height: ' +
                  (showSubjectCounts ? 300 : 400) +
                  'px;' +
                  ' display: ' +
                  (outcomeVar && cohorts && cohorts.length > 0
                    ? 'block'
                    : 'none')
              "
              :class="{ fullScreenView: showFullPanelView }"
            ></div>
            <div
              ref="echartContainer2"
              :style="
                'width: 100%; min-height: 100px;' +
                  ' display: ' +
                  (outcomeVar &&
                  cohorts &&
                  cohorts.length > 0 &&
                  showSubjectCounts
                    ? 'block'
                    : 'none')
              "
              :class="{ fullScreenView: showFullPanelView }"
            ></div>

            <div class="tooltipButton">
              <span class="pr-2">Show:</span>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-checkbox
                      v-model="showTooltips"
                      class="my-checkbox pa-0 ma-0"
                      label="tooltips"
                      dense
                      hide-details
                    ></v-checkbox>
                  </span>
                </template>
                <span v-if="true">Show tooltips</span>
              </v-tooltip>

              <v-tooltip v-if="isCategorical" top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-checkbox
                      v-model="showPercentages"
                      class="my-checkbox pa-0 ma-0 pl-3"
                      label="percentages"
                      dense
                      hide-details
                    ></v-checkbox>
                  </span>
                </template>
                <span v-if="true">Show percentages</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-checkbox
                      v-model="showSubjectCounts"
                      class="my-checkbox pa-0 ma-0 pl-3"
                      label="subject counts"
                      dense
                      hide-details
                    ></v-checkbox>
                  </span>
                </template>
                <span v-if="true">Show study group subject counts</span>
              </v-tooltip>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/dataExplorer/types';
import { format } from 'd3-format';
import { sortVisitEvents } from '@/utils/helpers';
import { colors } from '@/utils/colors';
import { sortByCategory } from '@/utils/categorical_vars';
import { mean } from 'd3-array';
import * as echarts from 'echarts';

export default {
  props: {
    showTitleBar: {
      type: Boolean,
      required: false,
      default: true,
    },
    showFullPanelView: {
      type: Boolean,
      required: false,
      default: false,
    },
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    cohorts: {
      type: Array,
      required: true,
      default: () => [],
    },
    outcomeVar: {
      type: Object,
      required: false,
      default: null,
    },
    labelFontSize: {
      type: Number,
      required: false,
      default: 16,
    },
  },
  data() {
    return {
      xaxis: 'visit_event',
      showTooltips: false,
      showPercentages: true,
      showSubjectCounts: true,
      // longitudinal graph (top)
      chart: null,
      chartOption: null,
      graphData: [],
      graphDataUpdated: false,
      graphCategories: [],
      legendData: [],
      // subject count graph (bottom)
      chart2: null,
      chartOption2: null,
      subjectCountData: [],
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      collection: state.COLLECTION,
      data: state.DATA,
      rawData: state.RAW_DATA,
      outcomeVariables: state.OUTCOME_VARIABLES,
      //      screenResize: state.SCREEN_RESIZE,
    }),
    isCategorical() {
      return this.outcomeVar && this.outcomeVar.data_category == 'Categorical';
    },
    axisFlippedNotNull() {
      return this.outcomeVar && this.outcomeVar.flip_axis != null;
    },
    axisFlipped() {
      return this.outcomeVar && this.outcomeVar.flip_axis;
    },
    obs_var() {
      var obs_v = null;
      this.collection.observation_variables_list.forEach(v => {
        if (v.ontology.id == this.outcomeVar.id) obs_v = v;
      });
      return obs_v;
    },
    fv() {
      return this.xaxis == 'visit_event'
        ? this.obs_var.first_visit_event
        : this.obs_var.first_visit_num;
    },
    lv() {
      return this.xaxis == 'visit_event'
        ? this.obs_var.last_visit_event
        : this.obs_var.last_visit_num;
    },
  },
  watch: {
    outcomeVar(ndv) {
      var obs_v = null;
      this.collection.observation_variables_list.forEach(v => {
        if (v.ontology.id == ndv.id) obs_v = v;
      });

      // determine if first/last visit is by event or number
      if (obs_v != null && obs_v.first_visit_event != null) {
        this.xaxis = 'visit_event';
      } else {
        this.xaxis = 'visit_num';
      }
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateGraphData();
      });
    },
    cohorts() {
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateGraphData();
      });
    },
    showTooltips(st) {
      this.analysis.showTooltips = st;
      if (typeof this.chartOption.tooltip != 'undefined') {
        this.chartOption.tooltip.show = st;
        this.chart.setOption(this.chartOption);
      }
      if (typeof this.chartOption2.tooltip != 'undefined') {
        this.chartOption2.tooltip.show = st;
        this.chart2.setOption(this.chartOption2);
      }
    },
    showPercentages(sp) {
      this.analysis.showPercentages = sp;
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateGraphData();
      });
    },
    showSubjectCounts(ssc) {
      this.analysis.showSubjectCounts = ssc;
      this.$nextTick(() => {
        this.onResize();
      });
    },
    graphData(gd) {
      if (gd.length > 0) {
        this.graphDataUpdated = true;
        this.makeGraphs();
      }
    },
    screenResize() {
      this.onResize();
    },
    analysis: {
      handler(a) {
        if (a.showTooltips != this.showTooltips)
          this.showTooltips = a.showTooltips;
        if (a.showPercentages != this.showPercentages)
          this.showPercentages = a.showPercentages;
        if (a.showSubjectCounts != this.showSubjectCounts)
          this.showSubjectCounts = a.showSubjectCounts;
      },
      deep: true,
    },
  },
  mounted() {
    this.showTooltips = this.analysis.showTooltips;
    this.showPercentages = this.analysis.showPercentages;
    this.showSubjectCounts = this.analysis.showSubjectCounts;
    this.graphDataUpdated = false;
    this.updateGraphData();
    this.initializeGraph();
  },
  methods: {
    onResize() {
      this.chart.resize();
      this.chart2.resize();
    },
    getXAccessor() {
      if (this.xaxis === 'visit_event') {
        return x => x.visit_event;
      } else {
        return x => x.visit_num;
      }
    },
    getVisits() {
      const rd = this.rawData;
      const visits = {};
      const xacc = this.getXAccessor();
      rd.forEach(x => {
        var xa = xacc(x);
        visits[xa] = 1;
      });
      return sortVisitEvents(Object.keys(visits));
    },

    updateGraphData() {
      if (!this.outcomeVar || !this.cohorts || this.cohorts.length == 0) return;
      let showPct = this.isCategorical && this.showPercentages;
      this.graphData = [];

      let visits = this.getVisits();
      const rd = this.rawData;

      // filter by observation_ontology_id,  group by subject_id
      const subj2data = {};
      rd.forEach(r => {
        if (this.outcomeVar.id == r.observation_ontology_id) {
          if (!(r.subject_id in subj2data)) {
            subj2data[r.subject_id] = [];
          }
          subj2data[r.subject_id].push(r);
        }
      });

      let gData = [];
      let subjCountData = [];

      // categorical data/counts indexed by category then cohort then visit
      let catData = {};
      // category totals indexed by cohort then visit
      let catTotals = {};

      // all observed categories
      let allCats = {};

      this.cohorts.forEach(c => {
        if (!(c.label in catTotals)) catTotals[c.label] = {};
        // subset of data pertaining to this cohort
        let cData = [];
        const subjids = [];
        c.subject_ids.forEach(sid => {
          subjids[sid] = 1;
          if (sid in subj2data) {
            cData.push(subj2data[sid]);
          }
        });

        // map visit to list of values
        let vis2data = {};
        cData.forEach(subjData => {
          subjData.forEach(sd => {
            let vkey = sd.visit_event;
            if (!(vkey in vis2data)) vis2data[vkey] = [];
            vis2data[vkey].push(sd['value']);
          });
        });

        let vData = [];
        let vSubjCounts = [];

        visits.forEach(v => {
          if (!(v in catTotals[c.label])) catTotals[c.label][v] = 0;
          if (v in vis2data) {
            if (this.isCategorical) {
              // generate counts
              let cts = {};
              vis2data[v].forEach(v => {
                if (!(v in cts)) {
                  if (!(v in allCats)) allCats[v] = 1;
                  cts[v] = 0;
                }
                cts[v]++;
              });

              Object.keys(cts).forEach(cat => {
                if (!(cat in catData)) catData[cat] = {};
                if (!(c.label in catData[cat])) catData[cat][c.label] = {};
                if (!(v in catData[cat][c.label]))
                  catData[cat][c.label][v] = cts[cat];
                catTotals[c.label][v] += cts[cat];
              });
            } else {
              vData.push(mean(vis2data[v]));
            }
            vSubjCounts.push(vis2data[v].length);
          } else {
            vData.push(null);
            vSubjCounts.push(0);
          }
        });

        if (!this.isCategorical) {
          gData.push({
            name: c.label.replace(/^\[O\]_/, ''),
            color: c.color,
            type: 'line',
            lineStyle: {
              width: 3,
            },
            connectNulls: true,
            symbolSize: 8,
            data: vData,
          });
        }

        subjCountData.push({
          name: c.label.replace(/^\[O\]_/, ''),
          color: c.color,
          type: 'bar',
          data: vSubjCounts,
        });
      });

      let legendData = null;

      if (this.isCategorical) {
        let sortedCats = sortByCategory(
          this.outcomeVar,
          Object.keys(allCats),
          a => a
        );

        sortedCats.forEach(sc => {
          this.cohorts.forEach(c => {
            let vData = catData[sc][c.label];

            let vValues = visits.map(v => {
              let vv = vData && v in vData ? vData[v] : null;
              if (showPct) {
                return vv == null ? 0 : (vv / catTotals[c.label][v]) * 100.0;
              } else {
                return vv;
              }
            });

            gData.push({
              name: sc,
              type: 'bar',
              stack: c.label,
              data: vValues,
            });

            //console.log("cat=" + sc + " cohort=" + c.label + " count=" + data.map(d => d.count) + " visits=" + data.map(d => d.visit));
          });
        });

        legendData = sortedCats;
      } else {
        legendData = [...this.cohorts.map(c => c.label.replace(/^\[O\]_/, ''))];
      }

      // add first and last visit markers
      gData.push({
        name: 'first visit',
        color: colors['firstVisit'],
        type: 'bar',
        stack: this.cohorts[0].label,
        data: [],
        markArea: {
          silent: false,
          data: [
            [
              { xAxis: this.obs_var.first_visit_event },
              { xAxis: this.obs_var.first_visit_event },
            ],
          ],
          itemStyle: {
            borderWidth: 0,
            opacity: colors['firstVisit-opacity'],
          },
        },
      });
      legendData.push('first visit');

      gData.push({
        name: 'last visit',
        color: colors['lastVisit'],
        type: 'bar',
        stack: this.cohorts[0].label,
        data: [],
        markArea: {
          silent: false,
          data: [
            [
              { xAxis: this.obs_var.last_visit_event },
              { xAxis: this.obs_var.last_visit_event },
            ],
          ],
          itemStyle: { borderWidth: 0, opacity: colors['lastVisit-opacity'] },
        },
      });
      legendData.push('last visit');

      this.graphData = gData;
      this.graphDataUpdated = true;
      this.legendData = legendData;
      this.subjectCountData = subjCountData;
    },
    initializeGraph() {
      var dom = this.$refs.echartContainer;
      this.chart = echarts.init(dom, null, {
        renderer: 'canvas',
        useDirtyRect: false,
      });
      var dom2 = this.$refs.echartContainer2;
      this.chart2 = echarts.init(dom2, null, {
        renderer: 'canvas',
        useDirtyRect: false,
      });
      this.makeGraphs();
      const resizeObserver = new ResizeObserver(() => {
        this.chart.resize();
        this.chart2.resize();
      });
      resizeObserver.observe(dom);
    },
    makeLongitudinalGraph() {
      let showPct = this.isCategorical && this.showPercentages;
      let valueFmtFn = function(value) {
        if (isNaN(value)) return 'N/A';
        if (showPct) {
          return format('.1f')(value) + '%';
        } else {
          return format('.1f')(value);
        }
      };

      let yaxisFmtFn = function(value) {
        return showPct ? value + '%' : value;
      };

      this.chartOption = {
        title: [
          {
            text:
              this.outcomeVar.abbreviation === this.outcomeVar.label
                ? this.outcomeVar.abbreviation
                : this.outcomeVar.abbreviation + ' | ' + this.outcomeVar.label,
            left: 'center',
            show: this.showTitleBar,
          },
        ],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          show: this.showTooltips,
          valueFormatter: x => valueFmtFn(x),
        },
        grid: {
          left: 75,
          right: 75,
          bottom: 70,
          top: 90,
          containLabel: false,
        },
        xAxis: {
          show: true,
          type: 'category',
          data: this.getVisits(),
          axisLabel: { fontSize: this.labelFontSize },
          //          name: 'Visit',
          //          nameLocation: 'middle',
          //          nameGap: 35,
          zlevel: 1,
        },
        yAxis: {
          show: true,
          type: 'value',
          axisLabel: {
            fontSize: this.labelFontSize,
            formatter: x => yaxisFmtFn(x),
          },
          name: this.isCategorical
            ? 'subjects'
            : this.axisFlippedNotNull
            ? 'Better <----> Worse'
            : this.outcomeVar
            ? this.outcomeVar.abbreviation
            : '',
          nameLocation: 'middle',
          nameGap: 45,
          inverse: this.axisFlipped,
          // force max to 100% when displaying percentages
          max: function() {
            return showPct ? 100.0 : null;
          },
        },
        legend: {
          data: this.legendData,
          selected: { detail: false },
          top: 30,
          left: 'center',
        },

        series: this.graphData,
        textStyle: { fontSize: this.labelFontSize, color: '#000000' },

        dataZoom: [
          {
            id: 'dataZoomX',
            type: 'slider',
            xAxisIndex: [0, 1],
            height: 25,
            bottom: 10,
            filterMode: 'none',
            showDataShadow: false,
            //            start: 10,
            //            end: 110,
          },
          {
            // show:false,
            id: 'dataZoomY',
            type: 'slider',
            yAxisIndex: [0],
            filterMode: 'none',
            start: 0,
            end: 100,
            right: 30,
            width: 25,
            showDataShadow: false,
            zlevel: 1,
            textStyle: {
              color: '#000',
            },
            labelFormatter: function() {
              return ' ';
            },
            dataBackground: {
              areaStyle: {
                color: '#333',
              },
            },
          },
        ],
      };
      this.chart.setOption(this.chartOption, true);
    },
    makeSubjectCountGraph() {
      this.chartOption2 = {
        title: null,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          show: this.showTooltips,
        },
        grid: {
          left: 75,
          right: 75,
          bottom: 10,
          top: 15,
        },
        xAxis: {
          show: false,
          type: 'category',
          data: this.getVisits(),
          axisLabel: { fontSize: this.labelFontSize },
          name: 'Visit',
          nameLocation: 'middle',
          nameGap: 35,
          zlevel: 1,
        },
        yAxis: {
          show: true,
          type: 'value',
          //          axisLabel: {
          //            fontSize: this.labelFontSize,
          //          },
          name: 'subjects',
          nameLocation: 'middle',
          nameGap: 45,
          inverse: false,
        },

        series: this.subjectCountData,
        textStyle: { fontSize: this.labelFontSize, color: '#000000' },
        dataZoom: [{ id: 'dataZoomX', show: false }],
      };
      this.chart2.setOption(this.chartOption2, true);
      echarts.connect([this.chart, this.chart2]);
    },
    makeGraphs() {
      // top
      this.makeLongitudinalGraph();
      // bottom
      this.makeSubjectCountGraph();
    },
  },
};
</script>

<style lang="scss" scoped>
.ttWrapper {
  position: relative;
}

.tooltipButton {
  position: absolute;
  top: 0rem;
  left: 1rem;
  margin: auto;
}

.fullScreenView {
  left: 0% !important;
  margin-top: 2rem;
}

.my-checkbox {
  display: inline-block;
}

::v-deep .my-checkbox .v-label {
  font-size: 15px;
}
</style>
